import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Check, Xmark, Mail } from 'iconoir-react';

interface Invitation {
  id: number;
  board_id: number;
  board_name: string;
  board_color: string | null;
  role: string;
  invited_by: string;
  created_at: string;
}

export const PendingInvitations: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await api.get('/invitations/pending');
      setInvitations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId: number) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/accept`);
      alert(response.data.message);
      
      // Recharger les invitations
      await loadInvitations();
      
      // Rediriger vers le tableau
      if (response.data.board) {
        navigate(`/boards/${response.data.board.id}`);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur lors de l\'acceptation de l\'invitation');
    }
  };

  const handleDecline = async (invitationId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser cette invitation ?')) return;

    try {
      await api.post(`/invitations/${invitationId}/decline`);
      await loadInvitations();
    } catch (error) {
      alert('Erreur lors du refus de l\'invitation');
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: { [key: string]: string } = {
      admin: 'Administrateur',
      editor: 'Éditeur',
      visitor: 'Visiteur',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colors: { [key: string]: string } = {
      admin: '#e74c3c',
      editor: '#3498db',
      visitor: '#95a5a6',
    };
    return colors[role] || '#95a5a6';
  };

  if (loading) {
    return null;
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <div className="pending-invitations">
      <div className="invitations-header">
        <Mail width={24} height={24} strokeWidth={2} />
        <h3>Invitations en attente ({invitations.length})</h3>
      </div>
      
      <div className="invitations-list">
        {invitations.map((invitation) => (
          <div key={invitation.id} className="invitation-card">
            <div className="invitation-info">
              <div 
                className="invitation-board-badge"
                style={{ 
                  backgroundColor: invitation.board_color || '#3498db',
                  color: 'white'
                }}
              >
                {invitation.board_name}
              </div>
              <div className="invitation-details">
                <p className="invitation-text">
                  <strong>{invitation.invited_by}</strong> vous invite à rejoindre ce tableau en tant que{' '}
                  <span 
                    className="invitation-role"
                    style={{ color: getRoleColor(invitation.role) }}
                  >
                    {getRoleLabel(invitation.role)}
                  </span>
                </p>
                <p className="invitation-date">
                  {new Date(invitation.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
            
            <div className="invitation-actions">
              <button
                className="btn btn-accept"
                onClick={() => handleAccept(invitation.id)}
                title="Accepter l'invitation"
              >
                <Check width={18} height={18} strokeWidth={2} />
                Accepter
              </button>
              <button
                className="btn btn-decline"
                onClick={() => handleDecline(invitation.id)}
                title="Refuser l'invitation"
              >
                <Xmark width={18} height={18} strokeWidth={2} />
                Refuser
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
